import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Phone, Mic, MessageSquare, Plus, Trash2, Pencil, Heart, ClipboardList, CalendarCheck, User as UserIcon, TrendingUp, UtensilsCrossed, Building2, Stethoscope, GraduationCap, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const TYPE_ICONS = { web_chat: MessageSquare, web_voice: Mic, voice_calling: Phone };

const TEMPLATE_ICONS = {
  blank: Plus,
  customer_support: Heart,
  lead_qualifier: TrendingUp,
  appointment_booker: CalendarCheck,
  info_collector: ClipboardList,
  sales_closer: TrendingUp,
  restaurant: UtensilsCrossed,
  real_estate: Building2,
  healthcare: Stethoscope,
  education: GraduationCap,
};

export default function AgentList() {
  const [agents, setAgents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [pickedChannels, setPickedChannels] = useState(["web_chat"]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [a, t] = await Promise.all([api.get("/agents"), api.get("/phone-numbers/templates")]);
    // Voice-calling assistants live on /dashboard/calls/assistants — keep them
    // out of this page so the two concepts don't leak into each other.
    setAgents((a.data || []).filter((x) => x.type !== "voice_calling"));
    setTemplates(t.data);
    if (!selectedTpl && t.data[0]) setSelectedTpl(t.data[0]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!selectedTpl || pickedChannels.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/agents", {
        name: name || selectedTpl.name,
        type: pickedChannels[0],
        channels: pickedChannels,
        system_prompt: selectedTpl.system_prompt,
        welcome_message: selectedTpl.welcome_message,
        voice_id: "alloy", language: "English", model: "gpt-4o-mini",
        temperature: 0.6, status: "active",
      });
      toast.success("Agent created");
      setOpen(false); setName("");
      nav(`/dashboard/agents/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    await api.delete(`/agents/${id}`); toast.success("Deleted"); load();
  };

  const rename = async (agent) => {
    // eslint-disable-next-line no-alert
    const next = window.prompt("Rename agent", agent.name);
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === agent.name) return;
    if (trimmed.length > 80) { toast.error("Name is too long (max 80)"); return; }
    try {
      const p = { ...agent, name: trimmed };
      delete p.id; delete p.tenant_id; delete p.created_at;
      await api.put(`/agents/${agent.id}`, p);
      toast.success("Renamed — visible on your widget on next load.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    }
  };

  const grouped = templates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div data-testid="agents-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Agents</h1>
          <p className="text-sm text-[#4B5563]">Build, test and deploy AI agents across channels.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-agent-button" className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Create Assistant</button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#0044FF]" /> Choose a template</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#4B5563]">Here are a few templates to get you started, or pick the blank slate.</p>

            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs font-medium block mb-1">Assistant Name <span className="text-[#4B5563] font-normal">(adjustable later)</span></label>
                <input
                  data-testid="new-agent-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={selectedTpl?.name || "New Assistant"}
                  className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm font-medium"
                />
              </div>

              <div>
                <label className="text-xs font-medium block mb-1">Channels <span className="text-[#4B5563] font-normal">(pick one or more — shared brain across all)</span></label>
                <div className="flex gap-2">
                  {[
                    { v: "web_chat", label: "Web Chat", Icon: MessageSquare },
                    { v: "web_voice", label: "Web Voice", Icon: Mic },
                  ].map(({ v, label, Icon }) => {
                    const on = pickedChannels.includes(v);
                    return (
                      <button
                        key={v}
                        data-testid={`new-agent-type-${v}`}
                        onClick={() => setPickedChannels(on ? pickedChannels.filter((x) => x !== v) : [...pickedChannels, v])}
                        className={`flex-1 border px-3 py-2 text-sm flex items-center justify-center gap-2 ${on ? "border-[#0044FF] bg-[#0044FF]/5 text-[#0044FF]" : "border-[#E2E8F0]"}`}
                      >
                        <Icon className="w-4 h-4" /> {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="mt-4">
                <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">{category}</div>
                <div className="grid grid-cols-2 gap-2">
                  {items.map((t) => {
                    const Icon = TEMPLATE_ICONS[t.id] || Bot;
                    const active = selectedTpl?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        data-testid={`template-${t.id}`}
                        onClick={() => setSelectedTpl(t)}
                        className={`text-left border p-3 transition-colors ${active ? "border-[#0044FF] bg-[#0044FF]/5" : "border-[#E2E8F0] hover:border-[#0044FF]/50"}`}
                      >
                        <Icon className={`w-5 h-5 ${active ? "text-[#0044FF]" : "text-[#4B5563]"}`} />
                        <div className="font-display font-bold text-sm mt-2">{t.name}</div>
                        <div className="text-[11px] text-[#4B5563] mt-1 line-clamp-2">{t.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
              <button data-testid="new-agent-create-button" onClick={create} disabled={busy || !selectedTpl} className="btn-primary text-sm">{busy ? "Creating…" : "Create Assistant"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {agents.length === 0 ? (
        <div className="hs-card p-12 text-center">
          <Bot className="w-10 h-10 mx-auto text-[#4B5563]" />
          <div className="font-display font-bold text-lg mt-4">No agents yet</div>
          <div className="text-sm text-[#4B5563]">Click "Create Assistant" to pick a template.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((a) => {
            const Icon = TYPE_ICONS[a.type] || Bot;
            return (
              <div key={a.id} className="hs-card p-6" data-testid={`agent-card-${a.id}`}>
                <div className="flex items-start justify-between">
                  <Icon className="w-7 h-7 text-[#0044FF]" />
                  <span className={`text-[10px] font-mono px-2 py-0.5 ${a.status === "active" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#4B5563]/10 text-[#4B5563]"}`}>{a.status}</span>
                </div>
                <div className="font-display font-bold text-xl mt-4">{a.name}</div>
                <div className="text-xs font-mono text-[#4B5563] mt-1">{(a.channels && a.channels.length ? a.channels : [a.type]).join(" · ")} · {a.language || "English"} · {a.voice_id}</div>
                <p className="text-sm text-[#4B5563] mt-3 line-clamp-2">{a.system_prompt}</p>
                <div className="flex items-center gap-2 mt-6">
                  <Link to={`/dashboard/agents/${a.id}`} className="btn-primary text-xs flex-1 text-center" data-testid={`agent-open-${a.id}`}>Open builder</Link>
                  <button onClick={() => rename(a)} className="btn-outline text-xs px-3" title="Rename agent" data-testid={`agent-rename-${a.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(a.id)} className="btn-outline text-xs px-3" data-testid={`agent-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
